import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Saca al usuario de una vista que quedó inservible (404, sin permisos,
 * `:id` inválido) sin dejarlo tirado ni echarlo fuera de la aplicación.
 *
 * Por qué no `location.back()` a secas: si la vista se abrió pegando la
 * URL, recargando (F5) o desde un mail, la entrada anterior del historial
 * NO es de la app — un `back()` lo mandaría al buscador o a Gmail. El
 * Router expone `lastSuccessfulNavigation.previousNavigation`, que solo
 * existe cuando hubo una navegación previa DENTRO de esta sesión de la
 * SPA: ese es el único caso en el que volver atrás es seguro.
 *
 * Volver con `Location.back()` (en lugar de navegar a la URL anterior)
 * además saca la ruta rota del historial; navegando la dejaríamos ahí y
 * el botón "atrás" del navegador la reabriría en loop.
 */
@Injectable({ providedIn: 'root' })
export class NavigationBackService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  /** `true` si el usuario llegó navegando dentro de la app. */
  hasInAppHistory(): boolean {
    return this.router.lastSuccessfulNavigation?.previousNavigation != null;
  }

  /**
   * Vuelve a la pantalla anterior de la app; si no hubo ninguna
   * (deep link o recarga), navega al `fallback`.
   */
  backOrFallback(fallback: string | readonly unknown[]): void {
    if (this.hasInAppHistory()) {
      this.location.back();
      return;
    }
    const commands = Array.isArray(fallback) ? fallback : [fallback];
    this.router.navigate(commands as unknown[]);
  }
}
